import {use, useCallback, useEffect} from 'react'

import {createRepositoryFileAttachment} from '../attachments/repository-file-attachment'
import {FileAttachContext, type FileAttachParams} from '../contexts/FileAttachContext'
import type {ComposerAttachmentsController} from './use-composer-attachments'

const RESOURCE_PANEL_ATTACHMENT_PREFIX = 'resource-panel-file:'

export function ComposerFileAttachBridge({
  attachments,
  writable,
}: {
  attachments: ComposerAttachmentsController
  writable: boolean
}) {
  useComposerFileAttach(attachments, writable)
  return null
}

export function useComposerFileAttach(attachments: ComposerAttachmentsController, writable: boolean) {
  const fileAttachRef = use(FileAttachContext)
  const addTrackedAttachments = attachments.addTrackedAttachments
  const attach = useCallback(
    (params: FileAttachParams) => {
      if (!writable) return
      addTrackedAttachments(
        [
          createRepositoryFileAttachment(
            {
              repo: {id: params.repoId, name: `${params.repoOwner}/${params.repoName}`, private: false},
              path: params.path,
              type: 'file',
              ref: params.ref,
              commitOID: params.commitOID,
            },
            RESOURCE_PANEL_ATTACHMENT_PREFIX,
          ),
        ],
        {replacePrefix: RESOURCE_PANEL_ATTACHMENT_PREFIX},
      )
    },
    [addTrackedAttachments, writable],
  )
  useEffect(() => {
    if (!fileAttachRef || !writable) return
    let active = true
    const handler = (params: FileAttachParams) => {
      if (active) attach(params)
    }
    fileAttachRef.current = handler
    return () => {
      active = false
      if (fileAttachRef.current === handler) fileAttachRef.current = null
    }
  }, [attach, fileAttachRef, writable])
}
